import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Wrapper, CardField, FieldLabel, PayButton, TabWrapper, Tab, TabItem, StyledVerifyButton } from './stripe-form-styles';
import { CONSTSTRINGS, ACH_PAYMENT_TAB, CREDIT_CARD_TAB } from './checkout-constants';
import ACHTab from './ach-tab';
import { useHistory } from 'react-router-dom';
import { payCC } from './stripe-functions';
import * as Sentry from '@sentry/browser';
import { Alert } from 'rsuite';
import { ERROR_MESSAGES } from '../components/common/error-constants';
import { strRegistrationApiSender } from '../utils/str-registration-api-sender';
import { Icon } from 'rsuite';

const CARD_OPTIONS = {
  style: {
    base: {
      fontWeight: 500,
      fontFamily: 'Nunito Sans, Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px',
    },
  },
};

function onCaughtError(err, { tot, receiptEmail, signatoryName }) {
  Sentry.withScope(function(scope) {
    scope.setUser({ id: tot, email: receiptEmail, username: signatoryName });
    Sentry.captureException(err);
  });
}

/**
 * Form where user selects payment method and enters payment information
 */
export function StripeForm({
  onSuccess,
  onSuccessRedirect,
  licenseFeeInfo,
  paymentPayload,
  setIsPaymentError,
  setActivePaymentMethodTab,
  activePaymentMethodTab,
  setPaymentErrorMessage,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();

  const [bankIdentifiers, setBankIdentifiers] = useState();
  const [lockPayment, setLockPayment] = useState(false);
  const [openLimitWarining, setOpenLimitWarning] = useState(false);

  const makeConfig = useCallback(async () => {
    const { data } = await strRegistrationApiSender.post('/getLinkToken');
    const linkToken = data.link_token;

    return {
      token: linkToken,
      onSuccess: async (publicToken, metadata) => {
        const accountId = metadata.accounts[0].id;
        const { data } = await strRegistrationApiSender.post('/plaidExchangeTokens', { publicToken, accountId });
        setBankIdentifiers({ bTok: data.stripe_bank_account_token, metadata });
      },
      onExit: () => setActivePaymentMethodTab(CREDIT_CARD_TAB),
    };
  }, []);

  // for locking immediately, without relying on async state change
  const lockRef = useRef(false);
  // for holding Plaid link instance
  const linkHandlerRef = useRef();

  useEffect(() => {
    async function initPlaid() {
      const configs = await makeConfig();
      linkHandlerRef.current = window.Plaid.create(configs);
    }
    initPlaid();
  }, []);

  const setLock = useCallback((value) => {
    lockRef.current = value;
    setLockPayment(value);
  }, [setLockPayment]);

  const handlePayCCSubmit = async () => {
    if (lockRef.current) return;
    setLock(true);

    payCC({
      stripe,
      elements,
      paymentPayload,
      onSuccess,
      onSuccessRedirect,
      unlock: () => {
        setLock(false);
      },
      history,
      onError: (err) => {
        onCaughtError(err, {
          paymentId: licenseFeeInfo.paymentId,
          receiptEmail: paymentPayload.email,
          signatoryName: paymentPayload.signatory,
        })
        setIsPaymentError(true)
        setPaymentErrorMessage(err);
      },
    });
  };

  const handlePayACHSubmit = useCallback(async () => {
    if (lockRef.current) return;
    setLock(true);

    try {
      const { data } = await strRegistrationApiSender.post('/payACH', {
        bTok: bankIdentifiers.bTok,
        metadata: bankIdentifiers.metadata,
        ...paymentPayload
      });
      await onSuccess(data);
      onSuccessRedirect();
    } catch (err) {
      // TODO: server error. Bc of this, we need to implement a way to come back to a payment.
      // The user must be able to check if the payment went through despite the error.
      console.error('Caught error:', err);
      onCaughtError(err, {
        paymentId: licenseFeeInfo.paymentId,
        receiptEmail: paymentPayload.email,
        signatoryName: paymentPayload.signatory,
      });
      Alert.error(ERROR_MESSAGES.PayACHError, 0)
      setLock(false);
    }
  }, [bankIdentifiers, licenseFeeInfo, history, paymentPayload, onSuccess, setLock]);

  return (
    <Wrapper>
      <FieldLabel
        style={{ marginBottom: 0 }}
        fontSize='16px'
      >
        {CONSTSTRINGS.PAYMENT_METHOD}
      </FieldLabel>
      <TabWrapper>
        <ACHTab
          isactive={activePaymentMethodTab === ACH_PAYMENT_TAB}
          achTotal={licenseFeeInfo.achTotal}
          setActiveTab={setActivePaymentMethodTab}
          openLimitWarning={openLimitWarining}
          setOpenLimitWarning={setOpenLimitWarning}
        />
        <Tab
          isactive={activePaymentMethodTab === CREDIT_CARD_TAB}
          onClick={() => setActivePaymentMethodTab(CREDIT_CARD_TAB)}
        >
          <TabItem>
            <img
              src='https://js.stripe.com/v3/fingerprinted/img/card-ce24697297bd3c6a00fdd2fb6f760f0d.svg'
              alt=''
              height='14px'
            />
          </TabItem>
          <TabItem>
            <span
              style={{
                width: '100%',
                height: '12px',
                fontSize: '12px',
                padding: '5px 0 2px',
                lineHeight: '1',
              }}
            >
              Credit or Debit Card
            </span>
          </TabItem>
        </Tab>
      </TabWrapper>
      {activePaymentMethodTab === CREDIT_CARD_TAB && (
        <>
          <FieldLabel>{CONSTSTRINGS.CARD_INFORMATION}</FieldLabel>
          <CardField>
            <CardNumberElement className='stripe-element' id='stripe-card-number' options={CARD_OPTIONS} />
          </CardField>
          <CardField>
            <CardExpiryElement className='stripe-element' id='stripe-expiry' options={CARD_OPTIONS} />
            <CardCvcElement className='stripe-element' id='stripe-cvc' options={CARD_OPTIONS} />
          </CardField>
        </>
      )}

      {activePaymentMethodTab === CREDIT_CARD_TAB && (
        <PayButton
          id='payButton'
          className="cc-pay-button"
          loading={lockPayment}
          disabled={lockPayment}
          onClick={handlePayCCSubmit}
        >
          Pay
        </PayButton>
      )}
      {activePaymentMethodTab === ACH_PAYMENT_TAB && (
        <>
          <StyledVerifyButton
            size="medium"
            onClick={() => {
              if (licenseFeeInfo.achTotal > 6000) { // 6k is ach amount limit for stripe right now
                setOpenLimitWarning(true);
              } else {
                linkHandlerRef.current.open();
              }
            }}
          >
            <div style={{ position: 'relative', textAlign: 'center'}}>
              <span>Verify your bank account</span>
              <Icon style={{ float: 'right' }} icon="lock" />
            </div>
          </StyledVerifyButton>
          {!(!bankIdentifiers) &&
            <PayButton
              id='payButton'
              className="ach-pay-button"
              type='button'
              loading={lockPayment}
              onClick={handlePayACHSubmit}
              disabled={!bankIdentifiers || lockPayment}
            >
              Pay
            </PayButton>
          }
        </>
      )}
    </Wrapper>
  );
}
